import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { getStationeryItemById } from 'src/redux/slices/stationery-item';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import StationeryItemDetails from '../stationery-item-details';
import { LoadingScreen } from '../../../components/loading-screen';
import { useAppDispatcher, useAppSelector } from '../../../redux/store';

type Props = {
  id: string;
};

export default function StationeryItemDetailView({ id }: Props) {
  const { item, status, error } = useAppSelector((state) => state.stationeryItem);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getStationeryItemById({ itemID: id }));
  }, [dispatch, id]);

  if (status === 'loading' || status === 'idle') {
    return <LoadingScreen />;
  }

  if (status === 'failure') {
    return (
      <ErrorContent
        filled
        title="Failed to load stationery item details"
        description={error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.stationeryItem.root}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Stationery Items
          </Button>
        }
        sx={{
          py: 10,
        }}
      />
    );
  }

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Stationery Item Details"
        links={[
          {
            name: 'Stationery Items',
            href: paths.dashboard.stationeryItem.root,
          },
          { name: 'Stationery Item Details' },
        ]}
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      />

      <StationeryItemDetails currentStationeryItem={item} />
    </Container>
  );
}
